import * as Menu from "@radix-ui/react-dropdown-menu";
import { Bell } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { notificationKey, notificationLink, useMarkRead, useNotifications } from "../features/notifications/useNotifications";
import { formatRelative, secondsSince } from "../lib/format";

/** Notifications bell (in-app only in M1): unread count from stored notifications. */
export function NotificationsBell() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const q = useNotifications();
  const { one, all } = useMarkRead();
  const unread = q.data?.unread ?? 0;
  const items = (q.data?.items ?? []).slice(0, 6);
  return (
    <Menu.Root>
      <Menu.Trigger
        aria-label={unread > 99 ? t("shell.notifications_label_capped") : t("shell.notifications_label", { count: unread })}
        className="relative flex size-11 items-center justify-center rounded-[var(--radius-card)] text-ink-2 hover:bg-elevated hover:text-ink"
      >
        <Bell aria-hidden="true" className="size-[20px]" />
        {unread > 0 && (
          <span aria-hidden="true" className="absolute right-1.5 top-1.5 flex min-w-[18px] items-center justify-center rounded-full bg-[image:var(--gradient-primary-button)] px-1 text-[11px] font-semibold leading-[18px] text-[var(--text-on-accent)]">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content align="end" sideOffset={8}
          className="z-50 w-96 rounded-[var(--radius-card)] border border-line bg-surface p-2 text-[14px] shadow-[var(--shadow-card)]">
          {items.length === 0 ? (
            <div className="p-3 text-ink-2">{t("notification.empty")}</div>
          ) : (
            items.map((n) => (
              <Menu.Item key={n.id}
                onSelect={() => {
                  if (!n.read_at) one.mutate(n.id);
                  const to = notificationLink(n);
                  if (to) void navigate(to);
                }}
                className="flex cursor-pointer gap-3 rounded-[var(--radius-control)] p-3 outline-none data-[highlighted]:bg-elevated">
                <span aria-hidden="true" className={`mt-1.5 size-2 shrink-0 rounded-full ${n.read_at ? "bg-transparent" : "bg-[var(--accent-primary)]"}`} />
                <span className="min-w-0">
                  <span className={`block ${n.read_at ? "text-ink-2" : "text-ink"}`}>{t(notificationKey(n), { ...n.params })}</span>
                  {n.created_at && <span className="block text-[12px] text-ink-2">{formatRelative(secondsSince(n.created_at), i18n.language)}</span>}
                </span>
              </Menu.Item>
            ))
          )}
          <Menu.Separator className="my-1 h-px bg-[var(--border-default)]" />
          <div className="flex items-center justify-between px-2 py-1">
            <Menu.Item onSelect={() => void navigate("/notifications")} className="cursor-pointer rounded px-1 py-1 text-[13px] text-violet-text outline-none data-[highlighted]:underline">
              {t("notification.view_all")}
            </Menu.Item>
            {unread > 0 && (
              <Menu.Item onSelect={() => all.mutate()} className="cursor-pointer rounded px-1 py-1 text-[13px] text-ink-2 outline-none data-[highlighted]:text-ink">
                {t("notification.mark_all_read")}
              </Menu.Item>
            )}
          </div>
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
}
